import type {
  StrippingFertilizationResponse,
  UpdateFertilizationRequest,
} from '@/services/stripping/types';
import { calculateIvcDate, calculateIvcDateLocal } from '@/utils/formatting';

import type { Values } from './types';

export function getStrippingDonorDisplay(
  fertilization: StrippingFertilizationResponse,
  showPooledDonors: boolean,
): string {
  if (showPooledDonors && fertilization.aspirationPool?.donorsInPool) {
    return `${fertilization.aspirationPool.name}: ${fertilization.aspirationPool.donorsInPool}`;
  }

  return fertilization.donorSchedule?.donor?.name ?? '';
}

export function calculateIvc(
  row: Pick<
    Values,
    | 'ivfNumber'
    | 'ivcDead'
    | 'ivcMissing'
    | 'ivcBroken'
    | 'ivcImmature'
    | 'ivcExtra'
  >,
): number | null {
  if (typeof row?.ivfNumber !== 'number') {
    return null;
  }

  const calcIvc =
    row.ivfNumber -
    row.ivcDead -
    row.ivcMissing -
    row.ivcBroken -
    row.ivcImmature +
    row.ivcExtra;

  return calcIvc;
}

export const nullToFalseFormatter = (params: {
  value: boolean | null;
}): string | boolean => {
  return params.value === null ? false : params.value.toString();
};

// Fertilization + 1, but never after today: a same-evening strip belongs to today.
export const getSuggestedIvcDate = (ivfDate: Date | string): Date => {
  const now = new Date();
  const today = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    12,
    0,
    0,
    0,
  );
  const nextDay = calculateIvcDateLocal(ivfDate);

  return nextDay < today ? nextDay : today;
};

export const transformStrippingData = (
  selectedRow: StrippingFertilizationResponse,
  isIvcDateNightFixEnabled = false,
) => {
  const defaultDate = selectedRow.ivfDate
    ? isIvcDateNightFixEnabled
      ? getSuggestedIvcDate(selectedRow.ivfDate)
      : calculateIvcDate(selectedRow.ivfDate)
    : new Date();

  return {
    hasFewMotileSperm:
      selectedRow.hasFewMotileSperm !== null
        ? selectedRow.hasFewMotileSperm
        : false,
    contaminationInFert:
      selectedRow.contaminationInFert !== null
        ? !!selectedRow.contaminationInFert
        : false,
    hasNoMotileSperm:
      selectedRow.hasNoMotileSperm !== null
        ? selectedRow.hasNoMotileSperm
        : false,
    hasPoorCytoplasmQuality:
      selectedRow.hasPoorCytoplasmQuality !== null
        ? selectedRow.hasPoorCytoplasmQuality
        : false,
    hasTwoCellCleavage:
      selectedRow.hasTwoCellCleavage !== null
        ? selectedRow.hasTwoCellCleavage
        : false,
    isFragile: selectedRow.isFragile !== null ? selectedRow.isFragile : false,
    isHardToStrip:
      selectedRow.isHardToStrip !== null ? selectedRow.isHardToStrip : false,
    ivfNumber: selectedRow.ivfNumber !== null ? selectedRow.ivfNumber : 0,
    ivcNumber: selectedRow.ivcNumber !== null ? selectedRow.ivcNumber : 0,
    ivcBroken: selectedRow.ivcBroken !== null ? selectedRow.ivcBroken : 0,
    ivcDead: selectedRow.ivcDead !== null ? selectedRow.ivcDead : 0,
    ivcExtra: selectedRow.ivcExtra !== null ? selectedRow.ivcExtra : 0,
    ivcImmature: selectedRow.ivcImmature !== null ? selectedRow.ivcImmature : 0,
    ivcMissing: selectedRow.ivcMissing !== null ? selectedRow.ivcMissing : 0,
    ivcComment: selectedRow.ivcComment !== null ? selectedRow.ivcComment : '',
    qcIncubator:
      selectedRow.qcIncubator !== null ? selectedRow.qcIncubator?.id : '',
    strippedAtDate: selectedRow.strippedAt
      ? new Date(selectedRow.strippedAt)
      : defaultDate,
    strippedAtTime: selectedRow.strippedAt
      ? new Date(selectedRow.strippedAt)
      : new Date(),
  } satisfies Values;
};

export const getUpdateFertilizationInput = (
  data: Values,
  combinedDateTime: Date,
  strippingTechnicianId: string,
  qcStripperTipLotNumber: string,
  formattedTime: string,
): Omit<UpdateFertilizationRequest, 'ianaTimeZone'> => {
  return {
    hasFewMotileSperm: data.hasFewMotileSperm ?? false,
    hasNoMotileSperm: data.hasNoMotileSperm ?? false,
    hasPoorCytoplasmQuality: data.hasPoorCytoplasmQuality ?? false,
    hasTwoCellCleavage: data.hasTwoCellCleavage ?? false,
    isFragile: data.isFragile ?? false,
    isHardToStrip: data.isHardToStrip ?? false,
    ivcBroken: data.ivcBroken ?? 0,
    ivcComment: data.ivcComment ?? '',
    ivcDead: data.ivcDead ?? 0,
    ivcExtra: data.ivcExtra ?? 0,
    ivcImmature: data.ivcImmature ?? 0,
    ivcMissing: data.ivcMissing ?? 0,
    ivfNumber: data.ivfNumber ?? 0,
    ivcNumber: data.ivcNumber ?? 0,
    ivcTechnicianId: strippingTechnicianId ?? '',
    qcIncubatorId:
      typeof data.qcIncubator === 'object' && data.qcIncubator !== null
        ? data.qcIncubator
        : (data.qcIncubator ?? ''),
    strippedAt: combinedDateTime.toISOString(),
    contaminationInFert: data.contaminationInFert ?? false,
    stripperTipLotNumber: qcStripperTipLotNumber ?? '',
    strippedAtTime: formattedTime,
  };
};
